/**
 * PatientAdminBoard — the ward census board. Mental model: erfassen (add a
 * new patient) is a CREATE action that puts someone new into the census —
 * it is not a per-patient control, so it lives as a "+" affordance on the
 * census list's own header, never as a standing card above the list. Once a
 * patient is in the census, select them to admit / transfer / discharge —
 * those act on one already-registered stay.
 *
 * Two panes: the census list, and a WORKING pane holding either the register
 * flow (what a nurse does when nobody is selected — it is not an empty state)
 * or the selected patient's stay. Whether both fit is decided by the BOARD's
 * own width, not the viewport's (`.census-board`, frontend/src/index.css) —
 * this board is mounted at three unrelated widths and a viewport breakpoint
 * got that wrong in the main app's pane. Too narrow for two, it drills down
 * one pane at a time, `boardNavigation.ts` holding which.
 *
 * SHARED, presentational/interactive component — this is the agent's whole
 * face (frontend/CLAUDE.md: "AI gates instead of augments" / no chatbot
 * standing in for the actual tool). Rendered in three homes so the board can
 * never drift from itself:
 *   - inline in the main app's Home detail pane, via PatientAdminBoardConnected,
 *   - full-screen as the patient-admin satellite app's own Home page,
 *   - full-screen in the patient-admin satellite app's Session page (opening
 *     the agent's own session shows the SAME board, not a transcript).
 * It owns its own data (usePatients/useEncounters/useLocations) — there is no
 * per-session state to restore, unlike the Care Planner/Compatibility boards, because
 * the census is a live shared resource, not a conversation artifact.
 *
 * A patient carries NO name (token + config-labelled attributes only, see
 * patientLabel.ts); placement truth lives in the FHIR Encounter/placement-
 * event history (useEncounters), not in the patient's registration
 * attributes, which never update after a transfer.
 *
 * Every data-fetching section handles the 4 states (frontend/CLAUDE.md):
 * ERROR+retry, LOADING-without-cache (skeleton), EMPTY, DATA.
 */

import { Fragment, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, UserRoundPlus, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { EmptyState, ErrorWithRetry, LoadingSkeleton } from '@/components/clinical'
import { PatientRegistrationForm } from '@/components/clinical/PatientRegistrationForm'
import { CensusSummary } from '@/components/clinical/CensusSummary'
import { OptionChips } from '@/components/clinical/OptionChips'
import { AttributeField } from '@/components/clinical/AttributeField'
import { useGuideRegister } from '@/hooks/useGuideRegister'
import { PatientPicker, PatientAvatar } from '@/components/clinical/PatientPicker'
import {
  usePatients,
  usePatientsConfig,
  usePatientsSchema,
  usePatientWithRevision,
  useUpdatePatientAttributes,
} from '@/hooks/usePatients'
import { AdmissionRefSection } from './AdmissionRefSection'
import { useLocations } from '@/components/admin/org/useOrg'
import type { Location } from '@/api/endpoints/org'
import {
  useEncounters,
  useStayConfig,
  useCensusAdmitPatient,
  useCensusTransferPatient,
  useCensusDischargePatient,
} from '@/hooks/useEncounters'
import { formatPatientLabel, humaniseAttributeKey } from '@/lib/patientLabel'
import { randomUUID } from '@/lib/uuid'
import { ApiError } from '@/api/client'
import {
  leafLocations,
  currentLocationId,
  activeEncounter,
  locationIndex,
  useEnumLabel,
  StayHeader,
  StayTimeline,
} from '@/components/clinical/encounterDisplay'
import {
  censusNavReducer,
  censusWorkPane,
  INITIAL_CENSUS_NAV,
} from './boardNavigation'
import type { PatientResponse, PatientAttributeViolation } from '@/api/endpoints/patients'
import { parsePatientAttributeViolations } from '@/api/endpoints/patients'
import { coerceToField, displayValue, readableFields, writableFields } from '@/api/patientSchema'
import type { PatientAttributeField } from '@/api/types'

interface PlacementFormProps {
  locations: Location[]
  onCancel: () => void
  onConfirm: (locationId: string) => void
  busy: boolean
  confirmLabel: string
  /**
   * Restating confirm step — builds "Transfer <patient> to <unit>?" from the
   * chosen unit's name. Required for Transfer (a frequent, easy-to-fat-finger
   * move onto the wrong unit); omitted for Admit, which opens a brand-new
   * stay and stays single-step. When set, the picker's own button no longer
   * fires the mutation directly — it advances to a second screen that
   * restates the patient + action and asks again, with its own Confirm/Cancel.
   */
  confirmPrompt?: (unitName: string) => string
}

/** Shared inline unit picker for Admit + Transfer — a leaf-location select
 *  plus Confirm/Cancel, no navigation, no dialog: the action stays on the
 *  same screen (no added clicks, frontend CLAUDE.md / Beverly's red line). */
function PlacementForm({ locations, onCancel, onConfirm, busy, confirmLabel, confirmPrompt }: PlacementFormProps) {
  const { t } = useTranslation()
  const [locationId, setLocationId] = useState('')
  const [confirming, setConfirming] = useState(false)

  if (confirming) {
    const unitName = locations.find((l) => l.id === locationId)?.name ?? locationId
    return (
      <div className="mt-3 space-y-3 rounded-[18px] border border-neutral-200 bg-neutral-50 p-4">
        <p className="text-[15px] font-medium text-neutral-900">{confirmPrompt?.(unitName)}</p>
        <div className="flex gap-2">
          <Button
            type="button"
            size="pill"
            disabled={busy}
            onClick={() => onConfirm(locationId)}
            className="min-h-11"
          >
            {busy ? t('patientAdmin.actions.submitting') : confirmLabel}
          </Button>
          <Button type="button" variant="ghost" size="pill" onClick={() => setConfirming(false)} className="min-h-11">
            {t('patientAdmin.actions.cancel')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-3 rounded-[18px] border border-neutral-200 bg-neutral-50 p-4">
      {/* The chips carry the group's accessible name, so the visible text is
          a <span> — a <label htmlFor> would dangle on the chip path. */}
      <span className="block text-[13px] font-semibold text-neutral-500">
        {t('patientAdmin.actions.chooseUnit')}
      </span>
      <OptionChips
        id="placement-location"
        legend={t('patientAdmin.actions.chooseUnit')}
        placeholder={t('patientAdmin.actions.chooseUnit')}
        options={locations.map((l) => ({ value: l.id, label: l.name }))}
        value={locationId}
        onChange={setLocationId}
        disabled={busy}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          size="pill"
          disabled={!locationId || busy}
          onClick={() => (confirmPrompt ? setConfirming(true) : onConfirm(locationId))}
          className="min-h-11"
        >
          {confirmPrompt ? t('patientAdmin.actions.continue') : busy ? t('patientAdmin.actions.submitting') : confirmLabel}
        </Button>
        <Button type="button" variant="ghost" size="pill" onClick={onCancel} className="min-h-11">
          {t('patientAdmin.actions.cancel')}
        </Button>
      </div>
    </div>
  )
}

interface DispositionFormProps {
  dispositions: string[]
  onCancel: () => void
  onConfirm: (disposition: string) => void
  busy: boolean
  /** Restating confirm step — builds "Discharge <patient> — <disposition>?".
   *  Discharge has no undo in this UI, so unlike Admit/Transfer this step is
   *  never optional. */
  confirmPrompt: (dispositionLabel: string) => string
}

function DispositionForm({ dispositions, onCancel, onConfirm, busy, confirmPrompt }: DispositionFormProps) {
  const { t } = useTranslation()
  const label = useEnumLabel()
  const [disposition, setDisposition] = useState('')
  const [confirming, setConfirming] = useState(false)

  // Config still loading, or the backend omitted the `stay` block — the
  // Discharge trigger is disabled in that case (see `!stayConfig` below),
  // but a stale button-enabled render or a config that loaded with an empty
  // dispositions list must not fall through to a dead select + dead confirm.
  if (dispositions.length === 0) {
    return (
      <div className="mt-3 space-y-3 rounded-[18px] border border-neutral-200 bg-neutral-50 p-4">
        <p className="text-[15px] text-neutral-700">{t('patientAdmin.detail.dispositionsUnavailable')}</p>
        <Button type="button" variant="ghost" size="pill" onClick={onCancel} className="min-h-11">
          {t('patientAdmin.actions.cancel')}
        </Button>
      </div>
    )
  }

  if (confirming) {
    return (
      <div className="mt-3 space-y-3 rounded-[18px] border border-neutral-200 bg-neutral-50 p-4">
        <p className="text-[15px] font-medium text-neutral-900">
          {confirmPrompt(label('disposition', disposition))}
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            size="pill"
            variant="destructive"
            disabled={busy}
            onClick={() => onConfirm(disposition)}
            className="min-h-11"
          >
            {busy ? t('patientAdmin.actions.submitting') : t('patientAdmin.actions.confirmDischarge')}
          </Button>
          <Button type="button" variant="ghost" size="pill" onClick={() => setConfirming(false)} className="min-h-11">
            {t('patientAdmin.actions.cancel')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-3 rounded-[18px] border border-neutral-200 bg-neutral-50 p-4">
      <span className="block text-[13px] font-semibold text-neutral-500">
        {t('patientAdmin.actions.chooseDisposition')}
      </span>
      <OptionChips
        id="discharge-disposition"
        legend={t('patientAdmin.actions.chooseDisposition')}
        placeholder={t('patientAdmin.actions.chooseDisposition')}
        options={dispositions.map((d) => ({ value: d, label: label('disposition', d) }))}
        value={disposition}
        onChange={setDisposition}
        disabled={busy}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          size="pill"
          variant="secondary"
          disabled={!disposition || busy}
          onClick={() => setConfirming(true)}
          className="min-h-11"
        >
          {t('patientAdmin.actions.continue')}
        </Button>
        <Button type="button" variant="ghost" size="pill" onClick={onCancel} className="min-h-11">
          {t('patientAdmin.actions.cancel')}
        </Button>
      </div>
    </div>
  )
}

/** The caller's COMPLETE writable view, as the PUT body.
 *
 *  Not a diff: a write is the caller's whole view, so every writable field the
 *  draft holds a value for is sent, typed as its schema declares it, and a
 *  field left EMPTY is OMITTED — which is how the record sheds it. Fields
 *  outside the writable view are not in `fields` and therefore never appear
 *  here, which is what leaves another profession's values (and a
 *  server-minted one) untouched by this save. */
function writableView(
  fields: PatientAttributeField[],
  draft: Record<string, string>,
): Record<string, unknown> {
  const view: Record<string, unknown> = {}
  for (const field of fields) {
    const value = (draft[field.key] ?? '').trim()
    if (value) view[field.key] = coerceToField(field, value)
  }
  return view
}

/** Whether the draft would change anything the record currently holds. */
function viewDiffers(patient: PatientResponse, view: Record<string, unknown>, fields: PatientAttributeField[]): boolean {
  return fields.some((f) => {
    const before = displayValue(patient.attributes[f.key]).trim()
    const after = displayValue(view[f.key]).trim()
    return before !== after
  })
}

interface AttributesSectionProps {
  /** The census row this panel opened — its `attributes` are the saved values
   *  the editor starts from. A save writes the server's answer straight back
   *  into this list cache (useUpdatePatientAttributes), so the read view
   *  below shows the accepted values without a second fetch. */
  patient: PatientResponse
  currentLocId: string | null
}

/**
 * Attribute editor + read view, driven by the patient's CURRENT unit's
 * `$schema` document. Nothing renders before that document arrives: which
 * attributes exist depends on the unit AND the caller's profession, so there
 * is no deployment-wide floor left to fall back on.
 *
 * This is a compact in-card editor over a row the board already holds, so it
 * composes `AttributeField` rather than handing the whole document to rjsf the
 * way PatientRegistrationForm does. Both read the SAME `$defs.create` shape,
 * which is what stops the two from disagreeing about which fields exist.
 *
 * Violations appear only where they are produced — a rejected save's 422
 * `violations[]`, rendered next to the field that caused it. A stored record is
 * never re-judged on read: the schema is resolved from the patient's CURRENT
 * unit, so a transfer would otherwise flip a verdict on bytes nobody touched.
 */
function AttributesSection({ patient, currentLocId }: AttributesSectionProps) {
  const { t } = useTranslation()
  const { data: schemaDoc } = usePatientsSchema(currentLocId)
  const updateAttributes = useUpdatePatientAttributes()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saveViolations, setSaveViolations] = useState<Record<string, PatientAttributeViolation>>({})
  const [staleWrite, setStaleWrite] = useState(false)
  // The read the edit is computed from, and the entity tag it carried. Enabled
  // only while editing: the census row is enough to READ from, and paying a
  // second per-patient request to display one is waste. Once the nurse opens
  // the editor the tag has to exist, because a complete-view replacement
  // without `If-Match` would overwrite whatever landed since the census loaded.
  const revision = usePatientWithRevision(editing ? patient.token : null)
  const saveEtag = revision.data?.etag ?? null

  // The EDITABLE fields: `$defs.create` for this caller at this unit. A
  // server-minted field is absent from that shape by construction, so the
  // editor needs no strategy check to leave one out.
  const editableFields = writableFields(schemaDoc)
  // The READABLE fields: `$defs.item`, which additionally carries the minted
  // token and anything else the caller may see but not write.
  const readFields = readableFields(schemaDoc)
  // Anything the record holds that neither shape names — a key a layer stopped
  // declaring. It is shown read-only rather than dropped: a later schema
  // revision must never make a stored value silently disappear from the panel
  // (anti-REDCap: schema changes never destroy data). It is also not writable,
  // and a PUT that omits it leaves it alone, because the server only tombstones
  // fields inside the caller's writable view.
  const orphanKeys = Object.keys(patient.attributes).filter(
    (k) => !readFields.some((f) => f.key === k),
  )
  const readKeys = [...readFields.map((f) => f.key), ...orphanKeys]

  const startEditing = () => {
    // The draft starts from the record's CURRENT values for the writable
    // fields only. A field the record does not hold starts empty, and leaving
    // it empty means "not recorded" on save. The effect below re-seeds it from
    // the authoritative read once that lands — including after a 412, which is
    // how the nurse sees what she is now editing on top of.
    setDraft(
      Object.fromEntries(editableFields.map((f) => [f.key, displayValue(patient.attributes[f.key])])),
    )
    setSaveViolations({})
    setStaleWrite(false)
    setEditing(true)
  }
  const cancelEditing = () => {
    setEditing(false)
    setSaveViolations({})
    setStaleWrite(false)
  }

  const editedPatient = revision.data?.patient
  useEffect(() => {
    if (!editing || !editedPatient) return
    setDraft(
      Object.fromEntries(
        editableFields.map((f) => [f.key, displayValue(editedPatient.attributes[f.key])]),
      ),
    )
    // `editableFields` is derived per render; keying the effect on the shape's
    // key list keeps it from re-seeding (and discarding the nurse's typing) on
    // every render while still following a real schema change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, editedPatient, editableFields.map((f) => f.key).join('\u0000')])

  const view = writableView(editableFields, draft)
  const hasChanges = viewDiffers(patient, view, editableFields)
  // 422 renders per-field and 412 renders as the conflict notice above; only
  // what has no surface of its own falls through to the generic line.
  const genericSaveError =
    updateAttributes.isError &&
    !(
      updateAttributes.error instanceof ApiError &&
      (updateAttributes.error.status === 422 || updateAttributes.error.status === 412)
    )

  const handleSave = () => {
    if (!hasChanges || updateAttributes.isPending || !saveEtag) return
    setStaleWrite(false)
    updateAttributes.mutate(
      { token: patient.token, attributes: view, etag: saveEtag },
      {
        onSuccess: () => {
          setEditing(false)
          setSaveViolations({})
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 422) {
            const violations = parsePatientAttributeViolations(err.extensions)
            setSaveViolations(Object.fromEntries(violations.map((v) => [v.field, v])))
          }
          // Somebody else wrote between the read this draft was computed from
          // and this save. The editor stays open on the server's values (the
          // re-seed effect above) so the correction can be re-applied on top of
          // what is actually stored, rather than silently replacing it.
          if (err instanceof ApiError && err.status === 412) {
            setStaleWrite(true)
            void revision.refetch()
          }
        },
      },
    )
  }

  /** What the server said about one rejected field.
   *
   *  `detail` is prose and optional: a validator that can name the field without
   *  quoting the rejected value sends none, because a rejected value can be
   *  clinical text and an error body travels further than the request did. The
   *  field and the code are always there, so they are what is shown instead. */
  const violationHint = (violation: PatientAttributeViolation): string =>
    violation.detail ?? `${violation.field}: ${violation.code}`

  return (
    <section className="rounded-[18px] border border-neutral-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-[17px] font-semibold tracking-tight text-neutral-900">
          {t('patientAdmin.detail.attributesTitle')}
        </h3>
        <Button
          type="button"
          size="pill"
          variant={editing ? 'outline' : 'secondary'}
          className="min-h-9 px-3 text-[13px]"
          onClick={editing ? cancelEditing : startEditing}
        >
          {editing ? t('patientAdmin.actions.cancel') : t('patientAdmin.actions.editAttributes')}
        </Button>
      </div>

      {!editing ? (
        readKeys.length === 0 ? (
          <p className="text-[13px] text-neutral-500">{t('patientAdmin.detail.noAttributes')}</p>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[15px]">
            {readKeys.map((key) => {
              const value = displayValue(patient.attributes[key]).trim()
              return (
                <Fragment key={key}>
                  <dt className="text-neutral-500">
                    {t(`patients.attributes.${key}`, humaniseAttributeKey(key))}
                  </dt>
                  <dd className="font-medium text-neutral-900">{value || '—'}</dd>
                </Fragment>
              )
            })}
          </dl>
        )
      ) : (
        <div className="space-y-4">
          {/* Every control here is a field of the caller's WRITABLE view, so a
              server-minted field cannot appear: it is absent from
              `$defs.create`. Its current token still renders in the read view
              above, and its linkage is corrected via AdmissionRefSection's own
              dialog, never this generic editor. */}
          {editableFields
            .map((field) => {
              const violation = saveViolations[field.key]
              return (
                <AttributeField
                  key={field.key}
                  field={field}
                  idPrefix="edit-attr"
                  labelClassName="block text-[13px] font-medium text-neutral-900"
                  value={draft[field.key] ?? ''}
                  onChange={(value) => setDraft((prev) => ({ ...prev, [field.key]: value }))}
                  hint={violation && <p className="text-[13px] text-tone-warn">{violationHint(violation)}</p>}
                />
              )
            })}
          {staleWrite && (
            <p role="alert" className="text-[13px] text-destructive">
              {t('patients.register.staleWrite')}
            </p>
          )}
          {genericSaveError && (
            <p className="text-[13px] text-destructive">{t('patientAdmin.errors.updateAttributesFailed')}</p>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              size="pill"
              disabled={!hasChanges || updateAttributes.isPending || !saveEtag}
              onClick={handleSave}
              className="min-h-11"
            >
              {updateAttributes.isPending ? t('patientAdmin.actions.submitting') : t('common.save')}
            </Button>
            <Button type="button" variant="ghost" size="pill" onClick={cancelEditing} className="min-h-11">
              {t('patientAdmin.actions.cancel')}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

type ActionMode = 'admit' | 'transfer' | 'discharge' | null

interface DetailPanelProps {
  patient: PatientResponse
  labelFormat: string | undefined
  locations: Location[] | undefined
}

function DetailPanel({ patient, labelFormat, locations }: DetailPanelProps) {
  const { t } = useTranslation()
  const label = useEnumLabel()
  const { data: encounters, isLoading, isError, isFetching, refetch } = useEncounters(patient.token)
  const stayConfig = useStayConfig()
  const admit = useCensusAdmitPatient()
  const transfer = useCensusTransferPatient()
  const discharge = useCensusDischargePatient()
  const [mode, setMode] = useState<ActionMode>(null)
  // One idempotency key per open form, not per submit: a nurse re-confirming
  // the SAME still-open form (e.g. after a client-side error whose server
  // outcome is unknown) reuses this key so the retry dedupes server-side
  // instead of minting a fresh key and creating a duplicate placement event.
  // Regenerated only when a NEW mode opens; a fresh key is minted next time
  // because the form closes (mode -> null) exclusively on mutation success.
  const idempotencyKeyRef = useRef<string | null>(null)
  const toggleMode = (m: ActionMode) => {
    const next = mode === m ? null : m
    setMode(next)
    if (next) idempotencyKeyRef.current = randomUUID()
  }

  const locationById = useMemo(() => locationIndex(locations), [locations])
  const leaves = useMemo(() => leafLocations(locations), [locations])

  const currentLocId = currentLocationId(encounters)
  // Transfer targets exclude the patient's own current unit — moving them
  // "to" where they already are isn't a real target (server-side this would
  // just be a same-unit transfer event, not a meaningful destination).
  const transferLeaves = useMemo(
    () => leaves.filter((l) => l.id !== currentLocId),
    [leaves, currentLocId],
  )
  const active = activeEncounter(encounters)
  const hasActiveStay = !!active
  // Fabric: opt the real Admit control into the capability registry so the
  // agent can spotlight it by guide_id. The button renders only when
  // !hasActiveStay, so register ONLY then — otherwise the registry would
  // advertise a control that isn't in the DOM (a spotlight would find no
  // target). The effect runs every render (hooks rules); `enabled` gates the
  // registration and unregisters when a stay opens.
  const admitGuideProps = useGuideRegister(
    {
      guideId: 'patient.admit.submit',
      surfaceId: 'home',
      kind: 'control',
      labelKey: 'patientAdmin.actions.admit',
    },
    undefined,
    !hasActiveStay,
  )
  const busy = admit.isPending || transfer.isPending || discharge.isPending
  // formatPatientLabel returns the token-derived codename immediately
  // whenever a token is passed (patient.token always is here) — a wardName
  // arg would never be reached, so this doesn't compute one.
  const patientDisplayLabel = formatPatientLabel(patient.attributes, labelFormat, patient.token)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <PatientAvatar selected />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
            {t('patients.title')}
          </p>
          <h2 className="truncate font-display text-[21px] font-semibold tracking-tight text-neutral-900">
            {patientDisplayLabel}
          </h2>
          {/* Codename provenance (nurse feedback #2): the name is not a real
              identifier — a small always-visible caption, not a tooltip that
              hides the explanation behind an interaction. */}
          <p className="mt-0.5 flex items-center gap-1 text-[12px] text-neutral-400">
            <Info className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span>{t('patientAdmin.detail.codenameExplanation')}</span>
          </p>
        </div>
      </div>

      {/* The stay — status, unit and how long they have been in. This surface
          OPENS the episode (ADR-0003), so it states the episode rather than
          listing an admission timestamp among attribute rows. */}
      <section className="rounded-[18px] border border-neutral-200 bg-white p-4">
        <StayHeader
          encounter={active}
          unitName={currentLocId ? (locationById.get(currentLocId)?.name ?? currentLocId) : undefined}
          title={t('patientAdmin.detail.currentPlacement')}
        />
        {hasActiveStay && (
          // Encounter-sourced facts ONLY. `bed` used to sit in this list and
          // it does not belong: bed is a registration ATTRIBUTE the nurse
          // maintains, and this file's own header records that attributes
          // never update on a transfer — so after a move the card headed
          // "current placement" showed the new unit beside the old bed, with
          // nothing marking which of the two was stale. The bed still renders,
          // in the attributes section below, where it is labelled as what it
          // is and can be corrected.
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[15px]">
            <dt className="text-neutral-500">{t('patientAdmin.detail.class')}</dt>
            <dd className="font-medium text-neutral-900">{label('class', active.class)}</dd>
            {active.responsible_service && (
              <>
                <dt className="text-neutral-500">{t('patientAdmin.detail.responsibleService')}</dt>
                <dd className="font-medium text-neutral-900">{active.responsible_service}</dd>
              </>
            )}
          </dl>
        )}

        {/* Actions */}
        <div className={cn('flex flex-wrap gap-2', hasActiveStay && 'mt-4')}>
          {!hasActiveStay && (
            <Button
              size="pill"
              className="min-h-11"
              disabled={busy}
              onClick={() => toggleMode('admit')}
              {...admitGuideProps}
            >
              {t('patientAdmin.actions.admit')}
            </Button>
          )}
          {hasActiveStay && (
            <>
              {/* Transfer is the routine, frequently repeated move — the
                  primary-weight action. Discharge ends the stay with no undo
                  in this UI: a muted, destructive-leaning outline (never the
                  loud Action-Blue) so a fast mis-tap can't land on it. */}
              <Button
                size="pill"
                className="min-h-11"
                disabled={busy}
                onClick={() => toggleMode('transfer')}
              >
                {t('patientAdmin.actions.transfer')}
              </Button>
              <Button
                size="pill"
                variant="outline"
                className="min-h-11 border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/5 hover:text-destructive"
                disabled={busy || !stayConfig}
                onClick={() => toggleMode('discharge')}
              >
                {t('patientAdmin.actions.discharge')}
              </Button>
            </>
          )}
        </div>

        {mode === 'admit' && (
          <PlacementForm
            locations={leaves}
            busy={admit.isPending}
            confirmLabel={t('patientAdmin.actions.confirmAdmit')}
            onCancel={() => setMode(null)}
            onConfirm={(locationId) => {
              admit.mutate(
                { patientId: patient.token, locationId, idempotencyKey: idempotencyKeyRef.current ?? undefined },
                { onSuccess: () => setMode(null) },
              )
            }}
          />
        )}
        {mode === 'transfer' && (
          <PlacementForm
            locations={transferLeaves}
            busy={transfer.isPending}
            confirmLabel={t('patientAdmin.actions.confirmTransfer')}
            confirmPrompt={(unitName) =>
              t('patientAdmin.actions.confirmTransferPrompt', { patient: patientDisplayLabel, unit: unitName })
            }
            onCancel={() => setMode(null)}
            onConfirm={(locationId) => {
              transfer.mutate(
                { patientId: patient.token, locationId, idempotencyKey: idempotencyKeyRef.current ?? undefined },
                { onSuccess: () => setMode(null) },
              )
            }}
          />
        )}
        {mode === 'discharge' && (
          <DispositionForm
            dispositions={stayConfig?.dispositions ?? []}
            busy={discharge.isPending}
            confirmPrompt={(dispositionLabel) =>
              t('patientAdmin.actions.confirmDischargePrompt', {
                patient: patientDisplayLabel,
                disposition: dispositionLabel,
              })
            }
            onCancel={() => setMode(null)}
            onConfirm={(disposition) => {
              discharge.mutate(
                { patientId: patient.token, disposition, idempotencyKey: idempotencyKeyRef.current ?? undefined },
                { onSuccess: () => setMode(null) },
              )
            }}
          />
        )}
      </section>

      <AttributesSection patient={patient} currentLocId={currentLocId} />

      <AdmissionRefSection patient={patient} currentLocId={currentLocId} />

      {/* Stay history */}
      <section>
        <h3 className="mb-3 font-display text-[17px] font-semibold tracking-tight text-neutral-900">
          {t('patientAdmin.detail.stayHistory')}
        </h3>

        {isError && !encounters ? (
          <ErrorWithRetry
            error={t('patientAdmin.detail.loadError.title')}
            onRetry={() => void refetch()}
          />
        ) : isLoading && !encounters ? (
          <LoadingSkeleton shape="card" count={2} />
        ) : encounters && encounters.length === 0 ? (
          <EmptyState
            title={t('patientAdmin.detail.noHistory')}
            description={t('patientAdmin.detail.noHistoryDescription')}
          />
        ) : (
          <div className="space-y-3">
            {isFetching && encounters && (
              <p className="text-[13px] text-neutral-500" role="status">
                {t('patientAdmin.detail.stale')}
              </p>
            )}
            <StayTimeline encounters={encounters ?? []} locationById={locationById} />
          </div>
        )}
      </section>
    </div>
  )
}

export interface PatientAdminBoardProps {
  className?: string
}

/** The ward census board's body: the census list with its "+ add new patient"
 *  header affordance (left/top), and the working pane — the register flow, or
 *  the selected patient's stay (right/bottom). No page chrome (title, back
 *  nav) — callers own that; this is the reusable face every surface renders
 *  identically.
 *
 *  Layout lives in `.census-board` (frontend/src/index.css): a CONTAINER
 *  query, because this same board is mounted full-screen and inside the main
 *  app's detail pane, and only the board's own width can decide whether two
 *  panes fit. Narrow, it drills down one pane at a time — a tap on a census
 *  row has to change what is on screen. */
export function PatientAdminBoard({ className }: PatientAdminBoardProps) {
  const { t } = useTranslation()
  const label = useEnumLabel()
  const [showDischarged, setShowDischarged] = useState(false)
  const { data: patients, isLoading, isError, isFetching, refetch } = usePatients(
    showDischarged ? 'all' : undefined,
  )
  const patientsConfig = usePatientsConfig()
  const { data: locations } = useLocations()
  const [nav, dispatch] = useReducer(censusNavReducer, INITIAL_CENSUS_NAV)
  const locationById = useMemo(() => locationIndex(locations), [locations])

  const workPane = censusWorkPane(nav)
  const selectedPatient =
    workPane.kind === 'detail'
      ? ((patients ?? []).find((p) => p.token === workPane.token) ?? null)
      : null

  // "The ward the nurse is looking at" — the strongest seed for the register
  // form's unit, ahead of her profile unit. Either the patient she has open,
  // or, when the whole census sits on ONE ward, that ward. A census spanning
  // several wards names none, and the form falls through to its next seed
  // rather than guessing.
  const contextLocationId = useMemo(() => {
    const selected = nav.selectedToken
      ? (patients ?? []).find((p) => p.token === nav.selectedToken)
      : undefined
    if (selected?.location_id) return selected.location_id
    const wards = new Set((patients ?? []).map((p) => p.location_id).filter(Boolean))
    return wards.size === 1 ? ([...wards][0] as string) : null
  }, [patients, nav.selectedToken])

  return (
    <div
      data-level={nav.level}
      className={cn('census-shell h-full bg-[var(--color-surface-canvas,#ffffff)]', className)}
    >
      <div className="census-board">
        <section className="census-pane census-pane-list px-4 py-4">
        {/* Census: the list IS the section — "register" is a create-new
            affordance on its header, not a standing per-patient card above
            it (registering only ever adds someone new; it never acts on an
            existing patient, so it must never read as part of the existing-
            patient list/management surface). It opens in the WORKING pane
            rather than inline: a full registration form squeezed above the
            list left the wide pane showing statistics while the actual job
            happened in a column a third its width. */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="font-display text-[17px] font-semibold tracking-tight text-neutral-900">
            {t('patientAdmin.list.title')}
          </h2>
          <Button
            size="pill"
            onClick={() => dispatch({ type: 'register' })}
            className="min-h-11 gap-1.5 px-4"
          >
            <UserRoundPlus className="h-4 w-4" aria-hidden="true" />
            {t('patientAdmin.list.addNew')}
          </Button>
        </div>

        <CensusSummary
          patients={patients}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          locationById={locationById}
          className="mb-3"
        />

        <label className="mb-3 flex items-center gap-2 text-[13px] text-neutral-600">
          <input
            type="checkbox"
            checked={showDischarged}
            onChange={(e) => setShowDischarged(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {t('patientAdmin.list.showDischarged')}
        </label>

        <PatientPicker
          patients={patients}
          labelFormat={patientsConfig?.label_format}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          onRetry={() => void refetch()}
          onSelect={(p) => dispatch({ type: 'select', token: p.token })}
          selectedToken={nav.selectedToken}
          locationById={locationById}
          renderBadge={(p) => (
            <>
              {/* A minted unit writes the reversal-index row at registration
                  and leaves it empty until someone supplies the case number,
                  so linked:false on a census row is "this admission has no
                  case number yet" — the one thing a nurse has to come back
                  to. Absent (every passthrough patient) is not false. */}
              {p.admission_ref_link?.linked === false && (
                <span className="shrink-0 rounded-full bg-tone-warn-tint px-2.5 py-0.5 text-[11px] font-semibold text-tone-warn">
                  {t('patientAdmin.detail.awaitingCaseNumber')}
                </span>
              )}
              {showDischarged && p.status !== 'in_progress' && (
                <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                  {label('status', p.status)}
                </span>
              )}
            </>
          )}
        />
        </section>

        <section className="census-pane census-pane-work px-4 py-4">
        {/* Back exists only in the narrow drill-down (`.census-back` is hidden
            once both panes fit) — the wide board never took the list away, so
            there is nothing to go back to. */}
        <button
          type="button"
          onClick={() => dispatch({ type: 'back' })}
          className="census-back -ml-2 mb-2 inline-flex min-h-11 items-center gap-1 rounded-full pl-1 pr-3 text-[15px] font-semibold text-primary transition active:scale-95"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          {t('patientAdmin.list.title')}
        </button>

        {workPane.kind === 'register' ? (
          <div className="mx-auto w-full max-w-xl">
            <h2 className="mb-4 font-display text-[21px] font-semibold tracking-tight text-neutral-900">
              {t('patients.register.title')}
            </h2>
            {/* `registered` records who was saved but keeps the pane on the
                form — the loop exits only on "Fertig". */}
            <PatientRegistrationForm
              contextLocationId={contextLocationId}
              onRegistered={(created) => dispatch({ type: 'registered', token: created.token })}
              onDone={() => dispatch({ type: 'doneRegistering' })}
            />
          </div>
        ) : selectedPatient ? (
          <DetailPanel
            key={selectedPatient.token}
            patient={selectedPatient}
            labelFormat={patientsConfig?.label_format}
            locations={locations}
          />
        ) : (
          // Selected, but not in the current list — the discharge filter hides
          // them, or a refetch dropped them. Say which, rather than blanking.
          <EmptyState
            title={t('patientAdmin.detail.notInList')}
            description={t('patientAdmin.detail.notInListDescription')}
          />
        )}
        </section>
      </div>
    </div>
  )
}
